import { BubbleDO } from "./bubble.models";
import { bubbleChartConfigs, BubbleChartConfig } from "./bubble.options";
import * as d3 from 'd3';
import { numberFormatters } from "../../core/formatters/index";
import { NumberType } from "../../core/formatters/number.formatter";
import { COLORS } from "../historical-charts/options/COLORS";
import './bubble.style.scss';
import { CtaComponent } from "../../call-to-action/cta.component";
import { capitalized } from "../../core/formatters/index";
import { LoaderComponent } from "../historical-charts/components/loader/loader-component";

interface BubbleNode extends d3.SimulationNodeDatum {
    id: string;
    value: number;
    type: string;
    x?: number;
    y?: number;
}

export class BubbleComponent extends HTMLElement {
    private _bubbles: BubbleDO;
    private svg: d3.Selection<SVGSVGElement, unknown, null, undefined>;
    private width: number = 400;
    private height: number = 300;
    private margin = { top: 0, right: 0, bottom: 0, left: 0 };
    private bubbleColors: string[] = [COLORS[0], COLORS[COLORS.length - 1]];
    private resizeDebounceTimeout: number | null = null;
    private cta: CtaComponent
    private loader: LoaderComponent
    private chartId: string
    private config: BubbleChartConfig
    private simulation: d3.Simulation<BubbleNode, undefined> | null = null;

    constructor() {
        super();
        this._bubbles = {};
        this.classList.add('tbw-bubble');
    }

    connectedCallback() {
        this.chartId = this.getAttribute('chart-id') || '';
        
        if (!this.chartId) { 
            console.error('No chart-id provided');
            return; 
        }

        this.config = this.getBubbleConfig();
        
        if (!this.config) { 
            console.error(`No configuration found for chart-id: ${this.chartId}`);
            return; 
        }

        // Apply custom options if provided
        if (this.config.options?.colors) {
            this.bubbleColors = this.config.options.colors;
        }

        this.initializeTemplate();
        this.initializeChart();
        this.setupEventListeners();
        
        // Create CTA component and initially hide it
        this.cta = document.createElement('tbw-cta') as CtaComponent
        this.appendChild(this.cta)
        this.cta.hide(); // Initially hide the CTA
        
        this.loadData();
    }

    getBubbleConfig(): BubbleChartConfig | undefined {
        return bubbleChartConfigs.get(this.chartId);
    }

    disconnectedCallback() {
        this.cleanupEventListeners();
        if (this.simulation) {
            this.simulation.stop();
        }
    }

    private initializeTemplate() {
        this.innerHTML = `
            <div class="bubble-chart-wrapper">
                <div class="bubble-chart-container"></div>
                <div class="tbw-loader"></div>
            </div>
            <div class="bubble-chart-legend"></div>
        `;
        
        // Initialize loader component
        const loaderElement = this.querySelector('.tbw-loader') as HTMLElement;
        if (loaderElement) {
            this.loader = new LoaderComponent(loaderElement);
            // Initially hide the loader
            this.loader.hide();
        }
    }

    private setupEventListeners() {
        window.addEventListener('authChange', this.onAuthChanged.bind(this));
        window.addEventListener('resize', this.onResize.bind(this));
    }

    private onAuthChanged = (e: Event) => {
        // Show loader when auth changes and data needs to be reloaded
        if (this.loader) {
            this.loader.show();
        }
        this.loadData();
    }

    private async loadData() {
        try {
            // Show loader while loading data
            if (this.loader) {
                this.loader.show();
            }
            
            // Get the configuration for this chart
            const config = this.getBubbleConfig();
            if (!config) {
                console.error('No configuration found for chart ID:', this.chartId);
                this.cta.show();
                if (this.loader) {
                    this.loader.hide();
                }
                return;
            }
            
            
            // Load data using the configured data loader
            const rawData = await config.dataLoader();
            
            // Check if data is valid
            if (!rawData || typeof rawData !== 'object') {
                this._bubbles = {};
                this.cta.show();
                if (this.loader) {
                    this.loader.hide();
                }
                return;
            }
            
            // Transform data if transformer is configured
            if (config.transformer) {
                this._bubbles = config.transformer(rawData);
            } else {
                this._bubbles = rawData;
            }
            
            // Hide loader
            if (this.loader) {
                this.loader.hide();
            }
            
            if (Object.keys(this._bubbles).length === 0) { 
                this.cta.show(); 
                return; 
            }
            
            this.updateChart();
            this.cta.hide();
        } catch (error) {
            console.error('Error loading bubble chart data:', error);
            
            // Hide loader on error
            if (this.loader) {
                this.loader.hide();
            }
            
            this.cta.show();
        }
    }

    private cleanupEventListeners() {
        window.removeEventListener('resize', this.onResize.bind(this));
        if (this.resizeDebounceTimeout) {
            window.clearTimeout(this.resizeDebounceTimeout);
        }
    }

    private onResize = () => {
        if (this.resizeDebounceTimeout) {
            window.clearTimeout(this.resizeDebounceTimeout);
        }
        this.resizeDebounceTimeout = window.setTimeout(() => {
            this.updateDimensions();
            this.updateChart();
        }, 250);
    }

    private updateDimensions() {
        const container = this.querySelector('.bubble-chart-container') as HTMLElement;
        if (container) {
            const containerRect = container.getBoundingClientRect();
            this.width = Math.max(containerRect.width, 200);
            this.height = Math.max(containerRect.height, 200);
        }
    }

    private initializeChart() {
        const container = this.querySelector('.bubble-chart-container') as HTMLElement;
        this.updateDimensions();
        this.svg = d3.select(container)
            .append('svg')
            .attr('width', '100%')
            .attr('height', '100%')
            .attr('viewBox', [0, 0, this.width, this.height])
            .attr('preserveAspectRatio', 'xMidYMid meet');
    }

    private createBubbleNodes(): BubbleNode[] {
        const innerWidth = this.width - this.margin.left - this.margin.right;
        const innerHeight = this.height - this.margin.top - this.margin.bottom;

        return Object.entries(this._bubbles)
            .filter(([key, value]) => {
                return key && 
                       value && 
                       typeof value === 'object' && 
                       typeof value.value === 'number' && 
                       typeof value.type === 'string';
            })
            .map(([key, value]) => ({
                id: key,
                value: value.value,
                type: value.type,
                x: innerWidth / 2,
                y: innerHeight / 2
            }));
    }

    private updateLegend(data: BubbleNode[], colorScale: d3.ScaleOrdinal<string, string>) {
        const legendContainer = this.querySelector('.bubble-chart-legend') as HTMLElement;
        if (!legendContainer) return;

        legendContainer.innerHTML = '';
        const uniqueTypes = Array.from(new Set(data.map(d => d.type).filter(type => type && typeof type === 'string')));

        uniqueTypes.forEach(type => {
            const legendItem = document.createElement('div');
            legendItem.className = 'legend-item';

            const colorBox = document.createElement('div');
            colorBox.className = 'color-box';
            colorBox.style.backgroundColor = colorScale(type);

            const label = document.createElement('span');
            label.className = 'legend-label';
            label.textContent = capitalized(type);

            legendItem.appendChild(colorBox);
            legendItem.appendChild(label);
            legendContainer.appendChild(legendItem);
        });
    }

    private createScales(data: BubbleNode[], innerWidth: number, innerHeight: number) {
        const colorScale = d3.scaleOrdinal(this.bubbleColors);
        const maxValue = d3.max(data, d => d.value) || 0;
        const minDimension = Math.min(innerWidth, innerHeight);
        const maxAllowedRadius = minDimension / 2 - 8;
        const minRadius = this.config.options?.minRadius || 0.085;
        let maxRadius = this.config.options?.maxRadius || 0.3;
        maxRadius = Math.min(maxRadius, maxAllowedRadius / minDimension);
        const radiusScale = d3.scaleSqrt()
            .domain([0, maxValue])
            .range([minDimension * minRadius, minDimension * maxRadius]);
        return { colorScale, radiusScale };
    }

    private setupSimulation(data: BubbleNode[], innerWidth: number, innerHeight: number, radiusScale: d3.ScalePower<number, number>) {
        const forceStrength = this.config.options?.forceStrength || 0.05;
        const collisionStrength = this.config.options?.collisionStrength || 0.04;
        return d3.forceSimulation<BubbleNode>(data)
            .force('center', d3.forceCenter(innerWidth / 2, innerHeight / 2))
            .force('charge', d3.forceManyBody().strength(forceStrength))
            .force('collide', d3.forceCollide<BubbleNode>().radius(d => radiusScale(d.value) + 2).strength(collisionStrength))
            .force('x', d3.forceX(innerWidth / 2).strength(forceStrength))
            .force('y', d3.forceY(innerHeight / 2).strength(forceStrength));
    }

    private calculateFontSize(value: number, baseSize: number, radiusScale: d3.ScalePower<number, number>) {
        const radius = radiusScale(value);
        return Math.min(Math.max(radius * baseSize, 8), 14);
    }

    private updateChart() {
        this.updateDimensions();
        if (!this.svg) return;
        if (this.simulation) {
            this.simulation.stop();
        }
        const innerWidth = this.width - this.margin.left - this.margin.right;
        const innerHeight = this.height - this.margin.top - this.margin.bottom;
        this.svg.attr('viewBox', [0, 0, this.width, this.height]);
        this.svg.selectAll('*').remove();
        const chartGroup = this.svg.append('g')
            .attr('transform', `translate(${this.margin.left},${this.margin.top})`);
        const data = this.createBubbleNodes();
        const { colorScale, radiusScale } = this.createScales(data, innerWidth, innerHeight);
        this.updateLegend(data, colorScale);
        this.simulation = this.setupSimulation(data, innerWidth, innerHeight, radiusScale);
        this.simulation.alpha(1).restart();
        const bubbles = chartGroup.selectAll('g')
            .data(data)
            .join('g')
            .attr('class', 'bubble')
            .attr('transform', d => `translate(${d.x},${d.y})`);
        bubbles.append('circle')
            .attr('r', d => radiusScale(d.value))
            .attr('fill', d => colorScale(d.type));
        bubbles.append('text')
            .attr('class', 'bubble-title')
            .text(d => capitalized(d.id.replace(/[_-]/g, ' ')))
            .attr('dy', '-0.5em')
            .attr('fill', 'white')
            .style('font-size', d => `${this.calculateFontSize(d.value, 0.3, radiusScale)}px`);
        bubbles.append('text')
            .attr('class', 'bubble-value')
            .text(d => numberFormatters.defaultFormat(d.value, NumberType.price))
            .attr('dy', '0.8em')
            .attr('fill', 'white')
            .style('font-size', d => `${this.calculateFontSize(d.value, 0.25, radiusScale)}px`);
        this.simulation.on('tick', () => {
            bubbles.attr('transform', d => {
                const r = radiusScale(d.value);
                const x = Math.max(r, Math.min(innerWidth - r, d.x!));
                const y = Math.max(r, Math.min(innerHeight - r, d.y!));
                return `translate(${x},${y})`;
            });
        });
    }
}